import { authenticatedFetch } from "./apiClient";
import { parseJsonOrThrow } from "../utils/apiRequestError";
import type {
  CreateDeploymentBody,
  DeployEvent,
  DeploySetup,
  DeploySignal,
  Deployment,
  DeploymentDetail,
} from "../types/strategyDeploy";
import { STRATEGY_RD_API_BASE, type StrategyRdHypothesis, type StrategyRdOhlcBar } from "../types/strategyRd";

const BASE = "/strategy-deploy";

async function call<T>(path: string, fallback: string, init?: RequestInit): Promise<T> {
  const response = await authenticatedFetch(`${BASE}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  return parseJsonOrThrow<T>(response, fallback);
}

/** The user's saved Lab strategies (backtests) — what the wizard can launch. */
export async function listLabStrategies(signal?: AbortSignal): Promise<StrategyRdHypothesis[]> {
  const response = await authenticatedFetch(`${STRATEGY_RD_API_BASE}/hypotheses`, { signal });
  const body = await parseJsonOrThrow<{ hypotheses?: StrategyRdHypothesis[] }>(response, "Could not load your strategies.");
  return Array.isArray(body.hypotheses) ? body.hypotheses.filter((h) => h && h.id) : [];
}

/** Recent candles from the central lane (the deployment's market). A window still filling
 *  (202) gives the candles already there — the worker keeps Launchpad markets fresh. */
export async function loadLaunchpadCandles(
  symbol: string,
  candleType: string,
  lookbackDays: number,
  signal?: AbortSignal
): Promise<StrategyRdOhlcBar[]> {
  const q = new URLSearchParams({
    symbol: symbol.trim(),
    candle_type: candleType.trim(),
    lookback_days: String(Math.max(1, Math.floor(lookbackDays))),
  });
  const response = await authenticatedFetch(`${STRATEGY_RD_API_BASE}/candles?${q.toString()}`, { signal });
  if (!response.ok && response.status !== 202) {
    await parseJsonOrThrow(response, "Could not load the chart.");
  }
  const body = (await response.json().catch(() => ({}))) as { candles?: unknown[] };
  const out: StrategyRdOhlcBar[] = [];
  for (const raw of Array.isArray(body.candles) ? body.candles : []) {
    const row = (raw ?? {}) as Record<string, unknown>;
    const timestamp = String(row.timestamp ?? row.time ?? row.bar_time ?? "").trim();
    const [open, high, low, close] = [row.open, row.high, row.low, row.close].map(Number);
    if (!timestamp || ![open, high, low, close].every(Number.isFinite)) continue;
    const volume = row.volume == null || row.volume === "" ? null : Number(row.volume);
    out.push({ timestamp, open, high, low, close, volume: Number.isFinite(volume) ? volume : null } as StrategyRdOhlcBar);
  }
  return out;
}

export const getDeploySetup = (signal?: AbortSignal) =>
  call<DeploySetup>("/setup", "Could not load your accounts.", { signal });

export const listDeployments = async (signal?: AbortSignal): Promise<Deployment[]> =>
  (await call<{ items: Deployment[] }>("/deployments", "Could not load your Launchpad strategies.", { signal })).items;

export const getDeployment = (id: string, signal?: AbortSignal) =>
  call<DeploymentDetail>(`/deployments/${encodeURIComponent(id)}`, "Could not load this deployment.", { signal });

export const createDeployment = (body: CreateDeploymentBody) =>
  call<Deployment>("/deployments", "Could not deploy the strategy.", { method: "POST", body: JSON.stringify(body) });

export const updateDeployment = (id: string, body: Partial<CreateDeploymentBody>) =>
  call<Deployment>(`/deployments/${encodeURIComponent(id)}`, "Could not save the settings.", {
    method: "PATCH",
    body: JSON.stringify(body),
  });

export const deploymentAction = (id: string, action: "pause" | "resume" | "stop" | "kill") =>
  call<Deployment>(`/deployments/${encodeURIComponent(id)}/${action}`, `Could not ${action} the deployment.`, {
    method: "POST",
  });

export const deleteDeployment = (id: string) =>
  call<{ ok: boolean }>(`/deployments/${encodeURIComponent(id)}`, "Could not delete the deployment.", {
    method: "DELETE",
  });

export const killAllDeployments = () =>
  call<{ killed: number }>("/kill-all", "Could not stop your Launchpad strategies.", { method: "POST" });

export const listDeploySignals = async (statuses?: string[], signal?: AbortSignal): Promise<DeploySignal[]> => {
  const q = statuses?.length ? `?status=${encodeURIComponent(statuses.join(","))}` : "";
  return (await call<{ items: DeploySignal[] }>(`/signals${q}`, "Could not load signals.", { signal })).items;
};

export const decideDeploySignal = (id: string, decision: "approve" | "reject") =>
  call<DeploySignal>(`/signals/${encodeURIComponent(id)}/${decision}`, `Could not ${decision} the signal.`, {
    method: "POST",
  });

export const listDeployEvents = async (opts: { limit?: number; beforeId?: number } = {}, signal?: AbortSignal) => {
  const q = new URLSearchParams({ limit: String(opts.limit ?? 120) });
  if (opts.beforeId) q.set("before_id", String(opts.beforeId));
  return (await call<{ items: DeployEvent[] }>(`/events?${q.toString()}`, "Could not load the log.", { signal })).items;
};

/** t.me deep link that binds this account to the Tessera bot (approve signals from Telegram). */
export const createTelegramLink = async (): Promise<string> => {
  const response = await authenticatedFetch("/notifications/telegram/link-token", { method: "POST" });
  const body = await parseJsonOrThrow<{ deeplink: string }>(response, "Could not create a Telegram link.");
  return body.deeplink;
};

export const getDeploymentLive = (id: string, signal?: AbortSignal) =>
  call<import("../types/strategyDeploy").DeploymentLive>(
    `/deployments/${encodeURIComponent(id)}/live`,
    "Could not load the live view.",
    { signal }
  );

export type ReportTradeSources = {
  deployments: {
    id: string;
    name: string;
    mode: string;
    symbol: string;
    candle_type: string;
    status: string;
    closed_trades: number;
    report_strategy_id?: string | null;
    broker_name?: string | null;
    broker_account_code?: string | null;
  }[];
  /** Newest first. */
  backtests: { id: string; name: string; version?: number | null; symbol?: string | null; candle_type?: string | null; closed_trades?: number | null; report_strategy_id?: string | null; applied_at?: string | null }[];
  /** Strategy id → name, for grouping. */
  strategies?: Record<string, string>;
};

/** Trades a report can analyse besides the broker journal: live strategies and Lab backtests. */
export const listReportTradeSources = (signal?: AbortSignal) =>
  call<ReportTradeSources>("/report-sources", "Could not load trade sources.", { signal });
